import { z } from 'zod';

const EnvSchema = z.object({
  apiBaseUrl: z.string().url('VITE_API_BASE_URL must be an absolute URL like https://api.example.com'),
  tenantSlug: z.string().min(1, 'VITE_TENANT_SLUG is required'),
  sentryDsn: z.string().optional().default(''),
  sentryEnvironment: z.string().min(1, 'VITE_SENTRY_ENVIRONMENT is required'),
  appVersion: z.string().min(1, 'VITE_APP_VERSION is required'),
  logLevel: z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal', 'silent']),
  // Optional: only populated in non-prod builds to unlock deterministic test OTPs.
  testOtpSecret: z.string().optional().default(''),
});

export type AppEnv = z.infer<typeof EnvSchema>;

/** Map a Zod issue path back to the public VITE_* var name we expect. */
const ZOD_PATH_TO_ENV_VAR: Record<string, string> = {
  apiBaseUrl: 'VITE_API_BASE_URL',
  tenantSlug: 'VITE_TENANT_SLUG',
  sentryDsn: 'VITE_SENTRY_DSN',
  sentryEnvironment: 'VITE_SENTRY_ENVIRONMENT',
  appVersion: 'VITE_APP_VERSION',
  logLevel: 'VITE_LOG_LEVEL',
  testOtpSecret: 'VITE_TEST_OTP_SECRET',
};

function loadEnv(): AppEnv {
  // Sensible defaults so an incomplete deploy still produces a helpful screen.
  const raw = {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    tenantSlug: import.meta.env.VITE_TENANT_SLUG ?? 'challengers-crease',
    sentryDsn: import.meta.env.VITE_SENTRY_DSN ?? '',
    sentryEnvironment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE ?? 'unknown',
    appVersion: import.meta.env.VITE_APP_VERSION ?? '0.0.0',
    logLevel: (import.meta.env.VITE_LOG_LEVEL ?? 'info') as AppEnv['logLevel'],
    testOtpSecret: import.meta.env.VITE_TEST_OTP_SECRET ?? '',
  };

  const result = EnvSchema.safeParse(raw);
  if (result.success) return result.data;

  const missing = result.error.issues.map((issue) => {
    const key = String(issue.path[0] ?? '');
    return ZOD_PATH_TO_ENV_VAR[key] ?? key;
  });
  const message =
    `Challengers Crease Admin failed to start because required environment variables are missing or invalid:\n` +
    `  ${missing.map((v) => `- ${v}`).join('\n  ')}\n\n` +
    `Build mode: ${import.meta.env.MODE}. Vite reads .env.${import.meta.env.MODE} (and .env.${import.meta.env.MODE}.local) at build time.\n` +
    `Either populate that file with VITE_* values, or pass them in your CI (e.g. VITE_API_BASE_URL=https://api.dev.ludimos.com yarn build).`;

  // Console for the developer, plus a visible fallback page so the user
  // doesn't see a blank screen.
  console.error('[cc-admin] env validation failed', result.error.issues);
  renderConfigError(message, missing);
  throw new Error(message);
}

function renderConfigError(message: string, missing: string[]): void {
  if (typeof document === 'undefined') return;
  const root = document.getElementById('app') ?? document.body;
  if (!root) return;
  root.innerHTML = `
    <div style="
      min-height: 100dvh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 32px 16px;
      font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
      background: #020618;
      color: #cbd5e1;
    ">
      <div style="max-width: 560px;">
        <div style="
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #8cf425;
          margin-bottom: 12px;
        ">Configuration error</div>
        <h1 style="
          color: #fff;
          font-family: 'Rajdhani', system-ui, sans-serif;
          font-size: 28px;
          margin: 0 0 16px;
        ">The dashboard couldn't start.</h1>
        <p style="margin: 0 0 16px; line-height: 1.6;">
          The build is missing required configuration. Set the following environment
          variable${missing.length === 1 ? '' : 's'} at build time and redeploy:
        </p>
        <ul style="
          background: rgba(7, 13, 29, 0.7);
          border: 1px solid #1e293b;
          border-radius: 12px;
          padding: 16px 24px;
          font-family: ui-monospace, SFMono-Regular, monospace;
          color: #fff;
          margin: 0 0 16px;
        ">
          ${missing.map((v) => `<li>${v}</li>`).join('')}
        </ul>
        <pre style="
          background: rgba(7, 13, 29, 0.7);
          border: 1px solid #1e293b;
          border-radius: 12px;
          padding: 16px;
          color: #94a3b8;
          font-size: 12px;
          white-space: pre-wrap;
          margin: 0;
        ">${message.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string))}</pre>
      </div>
    </div>
  `;
}

export const env: AppEnv = loadEnv();
